import React, { useState, useEffect, useCallback } from "react";
import { BiPlus, BiRefresh } from "react-icons/bi";
import { useSelector } from "react-redux";
import AddUsers from "./AddUsers";
import UserRow from "./UserRow";
import LoadingSpinner from "./LoadingSpinner";

const Users = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const { currentUser } = useSelector((state) => state.user);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const usersPerPage = 10;

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const fetchUsers = useCallback(async (pageNum = 1, isRefresh = false) => {
    try {
      isRefresh ? setLoading(true) : setLoadingMore(true);
      setError(null);
      
      const response = await fetch(
        `/api/user/users/${currentUser._id}?page=${pageNum}&limit=${usersPerPage}`
      );
      
      if (!response.ok) throw new Error("Failed to fetch users");
      
      const data = await response.json();
      
      if (isRefresh) {
        setUsers(data.users);
        setPage(1);
      } else {
        setUsers(prev => [...prev, ...data.users]);
      }
      
      setHasMore(data.page < data.pages);
    } catch (err) {
      console.error("Fetch users error:", err);
      setError(err.message || "Error fetching users");
    } finally {
      isRefresh ? setLoading(false) : setLoadingMore(false);
    }
  }, [currentUser._id, usersPerPage]);

  // Initial load or refresh
  useEffect(() => {
    fetchUsers(1, true);
  }, [lastRefresh, fetchUsers]);

  const handleRefresh = () => {
    setLastRefresh(new Date());
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchUsers(nextPage);
  };

  const handleUserAdded = () => {
    closeModal();
    handleRefresh();
  };

  return (
    <div className="tw-mt-10 tw-px-4 tw-py-2">
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4 tw-flex-wrap tw-gap-4">
        <h2 className="tw-text-2xl tw-font-semibold tw-text-sky-900">
          User Management
          {!loading && (
            <span className="tw-text-sm tw-font-normal tw-ml-2">
              ({users.length} {hasMore ? "+" : ""} users)
            </span>
          )}
        </h2>
        
        <div className="tw-flex tw-items-center tw-gap-4">        
          <button
            onClick={handleRefresh}
            className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-blue-800 tw-text-white tw-rounded-md hover:tw-bg-blue-700 tw-transition-colors"
            disabled={loading}
          >
            <BiRefresh className={`tw-text-lg ${loading ? "tw-animate-spin" : ""}`} />
            Refresh
          </button>

          <button
            onClick={openModal}
            className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-blue-800 tw-text-white tw-rounded-md hover:tw-bg-blue-700 tw-transition-colors"
          >
            <BiPlus className="tw-text-lg" /> 
            Add User
          </button>
        </div>
      </div>

      <div className="tw-overflow-x-auto tw-mb-6">
        <table className="tw-text-[16px] tw-text-sky-900 tw-bg-white tw-px-10 tw-py-4 tw-border-separate tw-border-spacing-y-2 tw-min-w-[800px] tw-w-full">
          <thead>
            <tr className="tw-bg-blue-800 tw-font-semibold tw-text-white">
              <th className="tw-p-2 tw-px-4">Name</th>
              <th className="tw-p-2 tw-px-4">Username</th>
              <th className="tw-p-2 tw-px-4">Phone</th>
              <th className="tw-p-2 tw-px-4">Balance</th>
              <th className="tw-p-2 tw-px-4">Cut</th>
              <th className="tw-p-2 tw-px-4">Role</th>
              <th className="tw-p-2 tw-px-4">Branch</th>
              <th className="tw-p-2 tw-px-4">Status</th>
              <th className="tw-p-2 tw-px-4">Min Bet</th>
              <th className="tw-p-2 tw-px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" className="tw-py-8">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="10" className="tw-text-center tw-py-4 tw-text-red-500">
                  {error}
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="10" className="tw-text-center tw-py-4">
                  No users found
                </td>
              </tr>
            ) : (
              <>
                {users.map((user) => (
                  <UserRow key={user._id} user={user} onUpdate={handleRefresh} />
                ))}
                {hasMore && (
                  <tr>
                    <td colSpan="10" className="tw-text-center tw-py-4">
                      <button
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        className="tw-px-4 tw-py-2 tw-bg-blue-100 tw-text-blue-800 tw-rounded-md hover:tw-bg-blue-200 tw-transition-colors"
                      >
                        {loadingMore ? (
                          <span className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                            <LoadingSpinner size="small" />
                            Loading...
                          </span>
                        ) : (
                          "Load More Users"
                        )}
                      </button>
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="tw-fixed tw-inset-0 tw-flex tw-justify-center tw-items-center tw-bg-gray-800 tw-bg-opacity-50 tw-z-50">
          <div className="tw-bg-white tw-p-6 tw-rounded-md tw-shadow-lg tw-relative tw-max-w-2xl tw-w-full tw-max-h-[90vh] tw-overflow-y-auto">
            <button
              className="tw-absolute tw-top-4 tw-right-4 tw-text-gray-500 hover:tw-text-gray-700 tw-text-2xl"
              onClick={closeModal}
            >
              &times;
            </button>
            <h3 className="tw-text-xl tw-font-semibold tw-mb-4">Add New User</h3>
            <AddUsers onSuccess={handleUserAdded} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;